import React, { useCallback, useState } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { SalesforceProfileModel } from "@api/types/salesforceprofile";
import api from "@api";
import { useSalesforceProfileStore } from "../../../../../../store/hooksy";
import { alertService } from "@services";
import { REGIONS } from "../../../../../../screens/request/steps/summary/views/summary/additional-form-modal/forms/salesforce/details";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<SalesforceProfileModel>;
}

type SalesforceProfileModelKey = keyof SalesforceProfileModel;

export const SalesforceProfileDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const [, setSalesforceProfileStore] = useSalesforceProfileStore();

  let salesforceProfile: SalesforceProfileModel = {
    id: 0,
    profileName: "",
    userType: "",
    isActive: true,
    region: "",
  };

  function clearAndCloseSalesforceProfileModel() {
    salesforceProfile.id = 0;
    salesforceProfile.profileName = "";
    salesforceProfile.userType = "";
    salesforceProfile.isActive = false;
    salesforceProfile.region = "";
    form.reset();
    onCancel();
  }

  const [isSubmitting, setSubmitting] = useState(false);

  // TODO: remove any type
  // function validate([name, value]: [SalesforceProfileModelKey, typeof name]): Promise<
  //   [SalesforceProfileModelKey, Error?]
  // > {
  //   switch (name) {
  //     case "profileName":
  //     case "region":
  //       return Promise.resolve([
  //         name as SalesforceProfileModelKey,
  //         Form.Validators.hasValue(value),
  //       ]);
  //     case "userType":
  //       return Promise.resolve([
  //         name as SalesforceProfileModelKey,
  //         Form.Validators.hasValue(value),
  //       ]);
  //     default:
  //       return Promise.resolve([name as SalesforceProfileModelKey]);
  //   }
  // }

  function validate(
    name: SalesforceProfileModelKey,
    value: SalesforceProfileModel[typeof name]
  ): Promise<[SalesforceProfileModelKey, Error?]> {
    switch (name) {
      case "profileName":
      case "region":
        return Promise.resolve([
          name as SalesforceProfileModelKey,
          Form.Validators.hasValue(value),
        ]);
      case "userType":
        return Promise.resolve([
          name as SalesforceProfileModelKey,
          Form.Validators.hasValue(value),
        ]);
      default:
        return Promise.resolve([name as SalesforceProfileModelKey]);
    }
  }

  const handleSubmit = useCallback(() => {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as SalesforceProfileModelKey, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);
      if (valid) {
        submitData();
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }, [form]);

  function submitData() {
    salesforceProfile.id = form.values.id;
    salesforceProfile.profileName = form.values.profileName;
    salesforceProfile.userType = form.values.userType;
    salesforceProfile.isActive = form.values.isActive;
    salesforceProfile.region = form.values.region;
    setSubmitting(true);
    if (salesforceProfile.id !== 0) {
      // EDIT
      api.salesforceprofile
        .putSalesforceProfile(salesforceProfile.id, salesforceProfile)
        .then((response) => {
          alertService.success({
            msg: "Salesforce Profile has been successfully updated.",
          });
          setSalesforceProfileStore({ fetchList: true });
          clearAndCloseSalesforceProfileModel();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      // CREATE
      api.salesforceprofile
        .postSalesforceProfile(salesforceProfile)
        .then((response) => {
          alertService.success({
            msg: "Salesforce Profile has been successfully saved.",
          });
          setSalesforceProfileStore({ fetchList: true });
          clearAndCloseSalesforceProfileModel();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
            console.log(error);
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function onChange(
    name: SalesforceProfileModelKey,
    value: SalesforceProfileModel[typeof name]
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const userTypes = [
    {
      label: "No Selection",
      value: "",
    },
    {
      label: "Sales",
      value: "Sales",
    },
    {
      label: "CSP (Service)",
      value: "CSP (Service)",
    },
    {
      label: "Sales Profile with CSP Access",
      value: "Sales Profile with CSP Access",
    },
    {
      label: "Docusign Access Only",
      value: "Docusign Access Only",
    },
    {
      label: "Corporate Marketing",
      value: "Corporate Marketing",
    },
  ];

  const regionOptions = [
    { label: "No Selection", value: "" },
    { label: REGIONS.AP, value: REGIONS.AP },
    { label: REGIONS.UK, value: REGIONS.UK },
    { label: REGIONS.CANADA, value: REGIONS.CANADA },
    { label: REGIONS.MNC, value: REGIONS.MNC },
    { label: REGIONS.CSP, value: REGIONS.CSP },
    { label: REGIONS.CSP_LATAM, value: REGIONS.CSP_LATAM },
    { label: REGIONS.MARKETING, value: REGIONS.MARKETING },
    { label: REGIONS.UK_ENTERPRISE, value: REGIONS.UK_ENTERPRISE },
    { label: REGIONS.MEXICO, value: REGIONS.MEXICO },
  ];

  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  return (
    <Modal
      isOpen={open}
      title={`${modalTitle} Salesforce Profile`}
      closeModal={() => clearAndCloseSalesforceProfileModel()}
      size="md"
    >
      <Form>
        <Form.Textbox
          label="Profile Name"
          name="profileName"
          onChange={onChange}
          size={12}
          error={form.errors.profileName}
          value={form.values.profileName}
          placeholder="Enter profile name"
          required
        />
        <Form.Dropdown
          label="User Type"
          onChange={onChange}
          options={userTypes}
          name="userType"
          error={form.errors.userType}
          value={form.values.userType}
          size={12}
          required
        />
        <Form.Dropdown
          label="Region"
          name="region"
          size={12}
          value={form.values.region}
          error={form.errors.region}
          options={regionOptions}
          onChange={onChange}
          required
        />
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          size={6}
          value={form.values.isActive}
        />
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearAndCloseSalesforceProfileModel()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
